import { createIntegration, MethodType, RedirectMethod, RedirectType } from '@endeavour/payment-integration';
import { getForm } from './utils/get-form.js';
import { PaymentField } from './method.interface.js';
import { z } from 'zod';

const INTEGRATION_ID = 'inovapay';
const createInovapayIntegration = ({ processPayment }) => {
    let sessionData = null;
    let getSession = null;
    const getSessionData = async () => {
        if (!getSession) {
            throw new Error('[Inovapay] No session provider');
        }
        if (!sessionData) {
            sessionData = await getSession(INTEGRATION_ID);
        }
        return sessionData;
    };
    return createIntegration({
        name: INTEGRATION_ID,
        async initialize(getIntegrationSession) {
            getSession = getIntegrationSession;
        },
        async destroy() {
            sessionData = null;
            getSession = null;
        },
        async getMethods({ methods }) {
            return methods.filter((method) => method.integrationId === INTEGRATION_ID);
        },
        async getSavedAccounts() {
            return [];
        },
        async getMethodDetailsForm({ providerId, type }) {
            const { providerData } = await getSessionData();
            const isAllMethods = providerId === INTEGRATION_ID;
            const isDeposit = type === MethodType.DEPOSIT;
            const fields = getForm(isDeposit, isAllMethods ? undefined : providerId, providerData);
            return {
                fields,
                schema: !isAllMethods
                    ? undefined
                    : z.union([
                        z.object({
                            [PaymentField.PAYMENT_METHOD]: z.literal('pix'),
                            [PaymentField.EMAIL]: z.string().min(1, 'email.error.required'),
                        }),
                        z.object({
                            // We're setting a min length but adding 'optional' so Zod knows to use this branch of the union
                            // when we switch payment method even if the other fields are not yet filled in.
                            // The requirement is fulfilled by the min length validator. This stops the form from displaying
                            // errors before the user has even tried to type a value.
                            [PaymentField.PAYMENT_METHOD]: z
                                .string()
                                .min(1, 'payment-method.error.required')
                                .optional(),
                            [PaymentField.EMAIL]: z
                                .string()
                                .min(1, 'email.error.required')
                                .optional(),
                            ...(isDeposit
                                ? {
                                    [PaymentField.ACCOUNT]: z
                                        .string()
                                        .min(1, 'branch.error.required')
                                        .optional(),
                                    [PaymentField.ACCOUNT_TYPE]: z
                                        .string()
                                        .min(1, 'branch.error.required')
                                        .optional(),
                                    [PaymentField.BRANCH]: z
                                        .string()
                                        .min(1, 'branch.error.required')
                                        .optional(),
                                }
                                : {}),
                        }),
                    ]),
            };
        },
        async processPayment({ providerId, type, formData }) {
            const { sessionId, providerData } = await getSessionData();
            if (!formData?.amount) {
                return {
                    success: false,
                    error: 'ERROR_AMOUNT_NOT_SPECIFIED',
                };
            }
            const externalPaymentMethodId = providerId === INTEGRATION_ID
                ? formData?.[PaymentField.PAYMENT_METHOD]
                : providerId;
            if (!externalPaymentMethodId) {
                return {
                    success: false,
                    error: 'ERROR_PAYMENT_METHOD_NOT_SPECIFIED',
                };
            }
            try {
                const { sessionContextId, cashierLaunchUrl } = await processPayment({
                    type: type === MethodType.DEPOSIT ? 'Deposit' : 'Withdrawal',
                    email: formData.email ?? providerData.email ?? '',
                    amount: formData.amount,
                    sessionContextId: sessionId,
                    externalPaymentMethodId,
                    branch: formData.branch,
                    account: formData.account,
                    accountType: formData.accountType,
                });
                return {
                    success: true,
                    transactionId: sessionContextId ?? sessionId,
                    redirectOutput: cashierLaunchUrl
                        ? {
                            url: cashierLaunchUrl,
                            method: RedirectMethod.GET,
                            container: RedirectType.IFRAME,
                            parameters: {},
                            width: 0,
                            height: 0,
                        }
                        : undefined,
                };
            }
            catch (e) {
                console.error(e);
                return {
                    success: false,
                    error: 'INOVAPAY_PROCESS_ERROR',
                    transactionId: sessionId,
                };
            }
        },
        getPendingWithdrawals() { },
        async cancelWithdrawal() {
            return false;
        },
    });
};

export { INTEGRATION_ID, createInovapayIntegration };
